import { NgClass } from '@angular/common';
import type { TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  ViewChild,
  output,
} from '@angular/core';

import { Subject } from 'rxjs';

import { RtAnalyticsModule, TrackDirective } from '@core/analytics-legacy';
import { FnCallPipe } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';

import { AvatarComponent } from '../avatar/avatar.component';
import type { AvatarShape, AvatarSize } from '../avatar/avatar.types';
import { AddNewButtonComponent } from '../button/components/add-new-button/add-new-button.component';
import { IconButtonComponent } from '../button/components/icon-button/icon-button.component';
import { GroupComponent } from '../group/group.component';
import type { TooltipPosition } from '../tooltip/tooltip-inner.component';
import { RtTooltip } from '../tooltip/tooltip.directive';
import type { Variants } from '../variant/variant';
import { ComponentVariant } from '../variant/variant';

export type AvatarUser = {
  id: number;
  hasAvatar: boolean;
  initials: string;
  photoThumbUrl: string;
  anonymizedAt?: string | null;
  isOnline?: boolean;
} & (
  | {
      name?: undefined;
      firstName: string;
      lastName: string;
    }
  | {
      name: string;
      firstName?: undefined;
      lastName?: undefined;
    }
);

@Component({
  selector: 'rt-admins-avatars',
  templateUrl: './admins-avatars.component.html',
  styleUrls: ['./admins-avatars.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.horizontal]': 'direction === "horizontal"',
    '[class.vertical]': 'direction === "vertical"',
    '[class.spacing--small]': 'spacing === "small"',
    '[class.spacing--small-x]': 'spacing === "small-x"',
    '[class.is-short]': 'isShort',
    '[class]': '[variant, "avatar-" + avatarSize]',
  },
  imports: [
    AddNewButtonComponent,
    AvatarComponent,
    GroupComponent,
    IconButtonComponent,
    NgClass,
    RtAnalyticsModule,
    RtTooltip,
    TrackDirective,
    FnCallPipe,
  ],
})
export class AdminsAvatarsComponent {
  public static readonly VARIANTS: Variants = ['ltr', ['default', 'ltr', 'rtl']];
  @ComponentVariant(AdminsAvatarsComponent.VARIANTS)
  @Input()
  public variant: string;
  @Input() public admins: AvatarUser[] = [];
  @Input() public avatarSize: AvatarSize = 'small';
  @Input() public avatarShape: AvatarShape = 'oval';
  @Input() public editable: boolean = false;
  @Input() public stacked: boolean = false;
  @Input() public isRole: boolean = false;
  @Input() public hasStopPropagation: boolean = true;
  @Input() public limit?: number;
  @Input() public direction: 'horizontal' | 'vertical' = 'horizontal';
  @Input() public spacing: 'small' | 'small-x' = 'small-x';
  @Input('tooltipPosition') public _tooltipPosition?: TooltipPosition;
  @Input() public tooltipMaxWidth?: number | null;
  @Input() public tooltipWordWrap: boolean = true;
  @Input() public renderAdminTooltip?: (admin: any) => string;
  @Input() public renderOtherAdminsTooltip?: (admins: any[]) => string;
  @Input() public themeContext: 'default' | 'overlay' = 'default'; // FIXME: is this used anywhere?
  @Input() public multiline: boolean = false;
  @Input() public isShort: boolean = false;
  @Input() public analyticsEvent?: string;
  @Input() public moreButtonAsCounter?: boolean = false;
  @Input() public counterAvatarTemplate?: TemplateRef<unknown>;
  @Input() public tooltipsEnabled = true;

  public avatarsClick = output<void>();
  public addClick = output<void>();
  public moreClick = output<void>();

  @ViewChild('moreButton', { read: ElementRef })
  public moreButton: ElementRef;

  public otherAdmins: AvatarUser[] = [];

  private ngOnDestroy$ = new Subject<void>();

  constructor(private i18n: I18nService) {}

  public get tooltipPosition(): TooltipPosition {
    return this._tooltipPosition || (this.otherAdmins.length > 2 ? 'below' : 'above');
  }

  public get iconButtonSize() {
    if (this.avatarSize === 'small-x') {
      return this.avatarSize;
    } else {
      return 'small';
    }
  }

  public ngOnChanges(): void {
    this.otherAdmins = this.admins.slice(this.limit || 0, this.admins.length);
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public visibleAssignments(): AvatarUser[] {
    return this.hasMore() ? this.admins.slice(0, this.limit || 0) : this.admins;
  }

  public hasMore(): boolean {
    return this.limit ? this.admins.length > this.limit : false;
  }

  public onAddClick(): void {
    if (this.editable) {
      this.addClick.emit();
    }
  }

  public onAvatarsClick(): void {
    if (this.editable) {
      this.avatarsClick.emit();
    }
  }

  public stopPropagation(event: MouseEvent): void {
    if (this.hasStopPropagation) {
      event.stopPropagation();
    }
  }

  public defaultAdminTooltip(admin: AvatarUser) {
    return admin.anonymizedAt
      ? this.i18n.translate('app.common.deleted_user')
      : this.getName(admin);
  }

  public getName(this: unknown, admin: AvatarUser) {
    return admin.name || `${admin.firstName} ${admin.lastName}`;
  }
}
